import { Badge } from '../primitives'
import { NodeThumb } from './NodeThumb'
import { TreeIcon } from './treeIcons'
import type { Shown } from './visible'
import { GAP, KIND, positionsText, qty, verdict } from './words'

/**
 * One node of the diagram: picture, name, revision, how it comes to exist, and
 * whether the shelf covers it.
 *
 * Three rows because those are the three questions, in the order people ask
 * them: *what is it*, *what kind of thing*, *have we got enough*. The quantity
 * per parent is not in the card at all — it sits on the connector, at the
 * point where the line meets the card, because "× 7" is a fact about the
 * *link* (an arm has seven of these), not about the actuator.
 *
 * The card is a real `<button>` (select it) and the collapse control is a
 * second, separate one beside it — never nested, which is invalid HTML and
 * leaves a screen reader announcing one button inside another.
 */
export function StructureCard({
  shown,
  imageId,
  selected,
  stockKnown,
  rootNote,
  onSelect,
  onToggle,
  onKeyDown,
}: {
  shown: Shown
  imageId: string | null
  selected: boolean
  stockKnown: boolean
  /** The root's third line — what the whole shelf adds up to. */
  rootNote: string | null
  onSelect: () => void
  onToggle: () => void
  onKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) => void
}): JSX.Element {
  const n = shown.node
  const root = n.depth === 0
  const consumed = n.leaf === 'consumed'
  const gap = n.leaf === null ? undefined : GAP[n.leaf]
  const v = verdict(n)
  const tone = !stockKnown || root ? '' : n.short > 0 ? ' st-node--short' : n.shortBelow > 0 ? ' st-node--blocked' : ''
  const open = n.children.length > 0 && shown.hidden === 0

  return (
    <div
      className={`st-node${tone}${selected ? ' st-node--selected' : ''}${consumed ? ' st-node--consumed' : ''}${root ? ' st-node--root' : ''}`}
    >
      {root ? null : (
        <span className="st-qty" aria-hidden="true">
          ×{qty(n.perParent)}
        </span>
      )}
      <button
        type="button"
        className="st-card"
        data-node={n.key}
        aria-pressed={selected}
        aria-expanded={n.children.length > 0 ? open : undefined}
        aria-label={`${root ? '' : `${qty(n.perParent)} × `}${n.typeName}${n.revisionName ? `, ${n.revisionName}` : ''}, ${
          KIND[n.kind].label
        }${stockKnown && !root ? `, ${n.onShelf} on shelf, need ${qty(n.need)}, ${v.label}` : ''}`}
        onClick={onSelect}
        onKeyDown={onKeyDown}
      >
        <NodeThumb imageId={imageId} kind={n.kind} consumed={consumed} root={root} size={root ? 56 : 48} />
        <span className="st-card__body">
          <span className="st-card__title">
            <span className="st-card__name">{n.typeName}</span>
            {n.revisionName ? <span className="st-card__rev">{n.revisionName}</span> : null}
          </span>
          <span className="st-card__sub">
            <Badge tone={KIND[n.kind].tone}>{KIND[n.kind].label}</Badge>
            {gap ? <Badge tone={gap.tone}>{gap.label}</Badge> : null}
            {n.positions.length > 0 ? (
              <span className="st-card__pos">{positionsText(n.positions)}</span>
            ) : consumed ? (
              <span className="st-card__pos">consumed</span>
            ) : null}
          </span>
          {root ? (
            rootNote ? <span className="st-card__stock">{rootNote}</span> : null
          ) : stockKnown ? (
            <span className="st-card__stock">
              <span className="st-card__counts">
                <strong>{qty(n.onShelf)}</strong> on shelf · need <strong>{qty(n.need)}</strong>
              </span>
              <Badge tone={v.tone}>{v.label}</Badge>
            </span>
          ) : null}
        </span>
      </button>
      {n.children.length > 0 ? (
        <button
          type="button"
          className={`st-toggle${!open && n.shortBelow > 0 && stockKnown ? ' st-toggle--alert' : ''}`}
          aria-label={open ? `Hide what ${n.typeName} is made of` : `Show what ${n.typeName} is made of`}
          aria-expanded={open}
          tabIndex={-1}
          onClick={onToggle}
        >
          {open ? <TreeIcon name="minus" size={14} /> : <span>+{shown.hidden}</span>}
        </button>
      ) : null}
    </div>
  )
}
